import { PVP_RUNE_PADS } from '@kenocyber/game-domain/pvp';
import type { PvpRuneWireKind } from '@kenocyber/shared';
import Phaser from 'phaser';
import {
  PVP_RUNE_HOVER_RADII,
  PVP_RUNE_RECIPES,
  type PvpRuneAppearance,
  type PvpRuneLayer,
  type PvpRuneLod,
  pvpRuneAppearance,
  pvpRuneCollectDurationMs,
  pvpRuneFloatOffset,
  pvpRuneLayerAlpha,
  pvpRuneLodAtLeast,
  pvpRuneLodForDiameter,
  pvpRuneOrbitPoint,
  pvpRunePadDiameterPx,
  pvpRunePulseScale,
  pvpRuneResidueCap,
  pvpRuneSealRadius,
  pvpRuneSweepScale,
  pvpRuneVariationTurns,
} from '../pvp/rune-appearance';
import { destroyRuneTextures, ensureAllRuneTextures, ensureRuneTexture } from './rune-art';

/**
 * O que desenha as runas na arena.
 *
 * ## O que ele substituiu
 *
 * Um `Phaser.GameObjects.Arc` por pad, criado com a arena e apenas recolorido.
 * Aquele disco tinha exatamente o raio de coleta, ficava na profundidade 6, e
 * existia mesmo com o pad vazio - o que fazia a runa ser uma COR numa área, e
 * não um objeto. Ele foi removido, e não coberto: um pad vazio agora não
 * desenha absolutamente nada.
 *
 * ## Quem manda em quê
 *
 * A SALA manda na runa. Este arquivo recebe o array autoritativo `runes` - o
 * mesmo `{ pad, kind }` que o socket carrega - e desenha o que ele disser.
 * Nunca decide que uma runa existe, qual é, quando foi pega ou quando volta.
 * Um pad cujo `kind` vai a `null` perde o artefato, e nenhuma animação o
 * segura.
 *
 * ## Tudo o que ele possui, ele destrói
 *
 * Sprites, o pool atrás deles, um emissor por família e as nove texturas
 * assadas. Nenhum tween e nenhum timer: a coleta é uma máquina de estados
 * movida pelo delta do quadro, que é uma coisa a menos para vazar.
 *
 * ## O que ele nunca faz por quadro
 *
 * Alocar. Nada de `Graphics` novo, array novo, closure nova ou configuração de
 * emissor por quadro - a lista de pads terminados é um rascunho reutilizado, e
 * a direção de cada partícula é escrita na própria partícula que o pool do
 * emissor já havia alocado.
 */

/**
 * Três profundidades, na folga que o produto deixou entre o pad e os obstáculos.
 *
 * `DEPTH.pad` era 6 e `DEPTH.obstacle` é 10, então 7, 8 e 9 estavam livres.
 * Desenhar ali é o que garante que um artefato nunca cubra um casco (30), um
 * canhão (31), um tiro (20), uma marca de equipe (35), uma barra de vida (40)
 * ou uma parede (10) - quem garante é a ordenação, não uma promessa sobre
 * tamanhos.
 */
export const RUNE_DEPTH = Object.freeze({ seal: 7, volume: 8, core: 9 });

/** O array autoritativo de runas, na forma exata em que o quadro o carrega. */
export interface RunePadState {
  readonly pad: number;
  readonly kind: PvpRuneWireKind | null;
}

/** O que o renderer possui neste instante. Lido por testes e pela bancada. */
export interface RuneRendererCensus {
  active: number;
  sprites: number;
  /**
   * Os planos de chão dos selos: um por pad ocupado.
   *
   * Contado à parte dos sprites porque é uma coisa diferente - um container não
   * desenha nada, ele transforma o que está dentro. Somá-lo aos sprites
   * esconderia a distinção justamente na métrica que existe para expor custo.
   */
  containers: number;
  pooled: number;
  emitters: number;
  particles: number;
  textures: number;
  drawing: Record<string, string>;
  lod: Record<string, PvpRuneLod>;
}

interface LayerInstance {
  readonly spec: PvpRuneLayer;
  readonly copy: number;
  readonly image: Phaser.GameObjects.Image;
}

type PadPhase = 'idle' | 'collecting';

interface PadVisual {
  readonly pad: number;
  readonly kind: PvpRuneWireKind;
  readonly appearance: PvpRuneAppearance;
  readonly x: number;
  readonly y: number;
  /** Um deslocamento por pad, derivado, para os quatro não balançarem juntos. */
  readonly phaseMs: number;
  layers: LayerInstance[];
  /**
   * O plano do chão em que o selo vive, e por que ele precisa de um dono.
   *
   * ## O defeito que este container conserta
   *
   * O selo é um disco DEITADO NO CHÃO que gira. Desenhá-lo era achatar um
   * círculo e girar o resultado - e no Phaser a matriz de um objeto é
   * `T · R · S`, então a escala entra primeiro e a rotação gira a elipse já
   * achatada. O que aparece na tela é uma elipse INCLINADA girando, cujo eixo
   * maior varre 360°, e não um disco no chão.
   *
   * A medição pegou isso com clareza: o centro luminoso da metade de baixo
   * oscilava de −20,8 px a +22,4 px conforme a fase, e a velocidade da
   * oscilação acompanhava exatamente o `spinTurnsPerSecond` de cada runa. Era o
   * anel varrendo de um lado para o outro.
   *
   * ## Por que um container resolve
   *
   * Um `Container` com `scaleY = squash` contendo o sprite com a rotação faz a
   * matriz composta virar `T · S_achatamento · R · S_tamanho`: a rotação
   * acontece PRIMEIRO, no plano do chão, e o achatamento vem depois. O eixo
   * maior da elipse fica horizontal para sempre; o que gira é o conteúdo dela -
   * que é o que um selo mágico deitado no chão faz.
   *
   * Custa um objeto por pad ocupado, quatro na arena, e é o único jeito de
   * expressar `S · R` com as transformações que um `Image` oferece.
   */
  sealPivot: Phaser.GameObjects.Container;
  seal: Phaser.GameObjects.Image;
  lod: PvpRuneLod | null;
  phase: PadPhase;
  collectMs: number;
  emitDebtMs: number;
  emitCount: number;
  flash: Phaser.GameObjects.Image | null;
}

/** O lado em que o ponto de resíduo é assado, de `rune-art.ts`. */
const MOTE_TEXTURE_SIZE = 32;

/**
 * Quanto de um raio de pad um ponto de resíduo pode ocupar, na escala 1.
 *
 * Um quarto, então o maior ponto que qualquer tipo declara tem uns 7 unidades de
 * mundo num pad de 120 - uma faísca, e não uma segunda runa. A fórmula anterior
 * dividia pela METADE do lado da textura e dobrava tudo: a primeira captura de
 * coleta do laboratório voltou como um anel de bolhas do tamanho da runa.
 */
const MOTE_DIAMETER_SHARE = 0.25;

/** O ângulo áureo, para direções de resíduo sucessivas nunca se amontoarem. */
const GOLDEN_TURN = 0.618_033_988_749_895;

const TAU = Math.PI * 2;

/** O mínimo que o renderer precisa da cena que o hospeda. */
export interface RuneRendererScene {
  add: {
    image(x: number, y: number, key: string): Phaser.GameObjects.Image;
    container(
      x: number,
      y: number,
      children?: Phaser.GameObjects.GameObject[],
    ): Phaser.GameObjects.Container;
    existing<T extends Phaser.GameObjects.GameObject>(child: T): T;
    particles(
      x: number,
      y: number,
      key: string,
      config: unknown,
    ): Phaser.GameObjects.Particles.ParticleEmitter;
  };
  textures: { exists(key: string): boolean; remove(key: string): unknown };
  make: { graphics(config: unknown, addToScene: boolean): Phaser.GameObjects.Graphics };
}

export class PvpRuneRenderer {
  private readonly scene: RuneRendererScene;
  /** O raio do pad em unidades de mundo: o que toda fração multiplica. */
  private readonly padRadius: number;
  private readonly pads = new Map<number, PadVisual>();
  private readonly pool: Phaser.GameObjects.Image[] = [];
  private readonly emitters = new Map<
    PvpRuneWireKind,
    Phaser.GameObjects.Particles.ParticleEmitter
  >();
  private readonly textureKeys: string[];
  /** Rascunho reutilizado: pads cuja coleta terminou neste quadro. */
  private readonly finished: number[] = [];

  /** O relógio do renderer, alimentado pelo delta do `update` da cena. */
  private clockMs = 0;
  private lastRunes: readonly RunePadState[] = [];
  private lastCorrection = 0;
  private destroyed = false;

  /**
   * @param padRadiusWorldUnits o raio de coleta desenhado, em unidades de mundo.
   * Passado em vez de importado para o renderer continuar sendo função dos seus
   * argumentos - a cena já converteu as milésimas do domínio uma vez.
   */
  constructor(scene: RuneRendererScene, padRadiusWorldUnits: number) {
    this.scene = scene;
    this.padRadius = padRadiusWorldUnits;
    this.textureKeys = ensureAllRuneTextures(scene, PVP_RUNE_RECIPES);
  }

  /* ---------------------------------------------------------------------- */
  /*  O que a sala diz                                                      */
  /* ---------------------------------------------------------------------- */

  /**
   * O estado autoritativo de cada pad.
   *
   * ## Coletada, ou simplesmente sumiu?
   *
   * Um pad que tinha runa e agora não tem é uma COLETA, e ganha os quatro
   * tempos. A não ser que o quadro seja uma descontinuidade - uma correção, um
   * reconnect, um mundo substituído, um F5 - caso em que o artefato sai sem
   * floreio nenhum, porque uma reconstrução a partir do estado atual tem de
   * mostrar o estado atual e nada mais. `correction` é o número que a sala já
   * publica exatamente para isso, então o renderer não precisa de uma regra
   * própria sobre o que é uma descontinuidade.
   */
  sync(runes: readonly RunePadState[], correction: number): void {
    if (this.destroyed) {
      return;
    }
    const discontinuous = correction !== this.lastCorrection;
    this.lastCorrection = correction;
    const previous = new Map(this.lastRunes.map((entry) => [entry.pad, entry.kind]));
    this.lastRunes = runes.map((entry) => ({ pad: entry.pad, kind: entry.kind }));

    if (discontinuous) {
      /* Resíduo de um mundo que não existe mais é uma mentira sobre este. */
      for (const emitter of this.emitters.values()) {
        emitter.killAll();
      }
      this.finished.length = 0;
      for (const visual of this.pads.values()) {
        if (visual.phase === 'collecting') {
          this.finished.push(visual.pad);
        }
      }
      for (const pad of this.finished) {
        this.retire(pad, 'immediate');
      }
    }

    for (const entry of runes) {
      const was = previous.get(entry.pad) ?? null;
      const now = entry.kind;
      if (was === now) {
        continue;
      }
      if (now === null) {
        this.retire(entry.pad, discontinuous ? 'immediate' : 'collect');
        continue;
      }
      /* Um pad que mudou de tipo troca o artefato de uma vez. */
      this.retire(entry.pad, 'immediate');
      this.raise(entry.pad, now);
    }
  }

  /**
   * Toda descontinuidade que a sala não expressa como número de correção.
   *
   * Reconnect, substituição de mundo, morte, abandono e estado terminal chegam
   * aqui. O resíduo morre, uma coleta pela metade para no meio do tempo, e o que
   * fica é exatamente o que o estado atual diz.
   */
  discontinuity(): void {
    if (this.destroyed) {
      return;
    }
    for (const emitter of this.emitters.values()) {
      emitter.killAll();
    }
    this.finished.length = 0;
    for (const visual of this.pads.values()) {
      if (visual.phase === 'collecting') {
        this.finished.push(visual.pad);
      }
    }
    for (const pad of this.finished) {
      this.retire(pad, 'immediate');
    }
  }

  /**
   * Todo artefato fora da arena de uma vez, sem floreio e sem resíduo.
   *
   * É o que uma arena FECHADA parece. `pvp:close` derruba a visão do duelo
   * enquanto a cena continua viva, então nada removeria um sprite por conta
   * própria - e um artefato pairando sobre uma arena que não existe mais é
   * exatamente o "arena silenciosamente parada" que este projeto recusa.
   */
  clear(): void {
    if (this.destroyed) {
      return;
    }
    for (const emitter of this.emitters.values()) {
      emitter.killAll();
    }
    for (const pad of [...this.pads.keys()]) {
      this.retire(pad, 'immediate');
    }
    this.lastRunes = [];
  }

  /* ---------------------------------------------------------------------- */
  /*  Levantar e recolher                                                   */
  /* ---------------------------------------------------------------------- */

  private raise(pad: number, kind: PvpRuneWireKind): void {
    const geometry = PVP_RUNE_PADS[pad];
    if (!geometry) {
      return;
    }
    const appearance = pvpRuneAppearance(kind);
    const x = geometry.x / 1000;
    const y = geometry.y / 1000;
    const seal = this.acquire();
    seal.setTexture(ensureRuneTexture(this.scene, appearance.seal.recipe));
    seal.setTint(appearance.palette.hot);
    seal.setBlendMode(Phaser.BlendModes.ADD);
    /*
     * O selo entra no seu plano, e a profundidade passa a ser do plano.
     *
     * Um filho de container não tem profundidade própria na display list da
     * cena - quem a tem é o container - então `RUNE_DEPTH.seal` vai para ele.
     * O sprite fica na origem local, que é o que faz a rotação acontecer em
     * torno do centro comum com o núcleo em vez de em torno de um ponto
     * qualquer.
     */
    const sealPivot = this.scene.add.container(x, y, [seal]);
    sealPivot.setDepth(RUNE_DEPTH.seal);
    seal.setPosition(0, 0);

    const visual: PadVisual = {
      pad,
      kind,
      appearance,
      x,
      y,
      phaseMs: pvpRuneVariationTurns(kind, pad) * 4000,
      layers: [],
      sealPivot,
      seal,
      lod: null,
      phase: 'idle',
      collectMs: 0,
      emitDebtMs: 0,
      emitCount: 0,
      flash: null,
    };

    for (const spec of appearance.layers) {
      const copies = spec.orbit ? Math.max(1, spec.orbit.count) : 1;
      for (let copy = 0; copy < copies; copy += 1) {
        const image = this.acquire();
        image.setTexture(ensureRuneTexture(this.scene, spec.recipe));
        image.setDepth(spec.band === 'core' ? RUNE_DEPTH.core : RUNE_DEPTH.volume);
        image.setTint(appearance.palette[spec.tint]);
        image.setBlendMode(spec.additive ? Phaser.BlendModes.ADD : Phaser.BlendModes.NORMAL);
        visual.layers.push({ spec, copy, image });
      }
    }

    this.pads.set(pad, visual);
    this.ensureEmitter(kind, appearance);
    /* Desenhado já, para uma captura tirada antes do próximo quadro estar certa. */
    this.drawPad(visual);
  }

  private retire(pad: number, how: 'immediate' | 'collect'): void {
    const visual = this.pads.get(pad);
    if (!visual) {
      return;
    }
    if (how === 'collect' && visual.phase === 'idle') {
      visual.phase = 'collecting';
      visual.collectMs = 0;
      return;
    }
    for (const instance of visual.layers) {
      this.release(instance.image);
    }
    visual.layers.length = 0;
    /*
     * O selo sai do seu plano antes de voltar ao pool, e o plano é destruído.
     *
     * Um sprite reciclado a partir do pool tem de poder ser posto de volta na
     * display list da cena; devolvê-lo ainda filho de um container o levaria
     * junto quando aquele container fosse destruído, e o pool ficaria com um
     * objeto morto. `removeAll(false)` tira o filho sem destruí-lo, que é
     * exatamente a distinção.
     */
    visual.sealPivot.removeAll(false);
    this.scene.add.existing(visual.seal);
    visual.sealPivot.destroy();
    this.release(visual.seal);
    if (visual.flash) {
      this.release(visual.flash);
      visual.flash = null;
    }
    this.pads.delete(pad);
  }

  /* ---------------------------------------------------------------------- */
  /*  O pool                                                                */
  /* ---------------------------------------------------------------------- */

  /**
   * Um sprite, do pool ou recém-feito.
   *
   * Reaproveitado em vez de criado porque uma runa volta a cada dez segundos em
   * quatro pads durante dois minutos, e criar uma dúzia de `Image` a cada vez
   * são vinte e quatro alocações por minuto no meio de uma luta. Um sprite
   * reciclado é reposto por completo - textura, tint, mistura, escala, rotação,
   * alfa - para nada da vida anterior vazar para esta.
   */
  private acquire(): Phaser.GameObjects.Image {
    const recycled = this.pool.pop();
    if (recycled) {
      recycled.setVisible(true).setActive(true).setScale(1).setRotation(0).setAlpha(1);
      recycled.setBlendMode(Phaser.BlendModes.NORMAL);
      return recycled;
    }
    const image = this.scene.add.image(0, 0, this.textureKeys[0] ?? '');
    image.setOrigin(0.5, 0.5);
    return image;
  }

  private release(image: Phaser.GameObjects.Image): void {
    image.setVisible(false).setActive(false);
    image.setPosition(0, 0);
    this.pool.push(image);
  }

  /* ---------------------------------------------------------------------- */
  /*  Resíduo                                                               */
  /* ---------------------------------------------------------------------- */

  /**
   * Um emissor por família, feito quando a família desenha pela primeira vez.
   *
   * Quatro pads mostrando Velocidade dividem um emissor; um quinto dividiria
   * também. O teto é `maxAliveParticles`, tirado da aparência e multiplicado
   * pelo número de pads que poderiam legitimamente carregá-la ao mesmo tempo -
   * quatro, e a arena não tem um quinto pad.
   */
  private ensureEmitter(
    kind: PvpRuneWireKind,
    appearance: PvpRuneAppearance,
  ): Phaser.GameObjects.Particles.ParticleEmitter {
    const existing = this.emitters.get(kind);
    if (existing) {
      return existing;
    }
    const pad = this.padRadius;
    const residue = appearance.residue;
    const emitter = this.scene.add.particles(0, 0, ensureRuneTexture(this.scene, 'mote'), {
      lifespan: residue.lifespanMs,
      speed: 0,
      alpha: { start: residue.startAlpha, end: 0 },
      scale: {
        start: (residue.scale * pad * MOTE_DIAMETER_SHARE) / MOTE_TEXTURE_SIZE,
        end: 0,
      },
      tint: appearance.palette.hot,
      blendMode: Phaser.BlendModes.ADD,
      maxAliveParticles: residue.maxParticles * PVP_RUNE_PADS.length,
      quantity: 1,
      /* Manual: cada partícula é posicionada e apontada por este arquivo. */
      frequency: -1,
      emitting: false,
    });
    emitter.setDepth(RUNE_DEPTH.volume);
    this.emitters.set(kind, emitter);
    return emitter;
  }

  /**
   * Uma partícula de resíduo, posicionada e apontada sem fonte aleatória.
   *
   * A direção caminha pelo ângulo áureo e o raio caminha por três vezes ele,
   * dobrado - então nem as direções se amontoam nem o estouro sai como um anel
   * perfeito, e duas máquinas que emitiram a mesma contagem emitem igual.
   */
  private emitResidue(visual: PadVisual, count: number, speedScale: number): void {
    const emitter = this.emitters.get(visual.kind);
    if (!emitter) {
      return;
    }
    const pad = this.padRadius;
    const residue = visual.appearance.residue;
    const hover = PVP_RUNE_HOVER_RADII * pad;
    for (let index = 0; index < count; index += 1) {
      const turn = (visual.emitCount * GOLDEN_TURN) % 1;
      visual.emitCount += 1;
      const angle = turn * TAU;
      const spread = 0.55 + 0.45 * ((((visual.emitCount * GOLDEN_TURN * 3) % 1) + 1) % 1);
      const radius = residue.spawnRadius * pad * spread;
      const x = visual.x + Math.cos(angle) * radius;
      /* Em volta do artefato, que flutua, e não da marca no chão. */
      const y = visual.y - hover + Math.sin(angle) * radius * 0.7;
      const particle = emitter.emitParticle(1, x, y);
      if (!particle) {
        continue;
      }
      /*
       * Apontada NA partícula, e não no emissor.
       *
       * Mexer em `speed` no emissor seria uma mudança de configuração por
       * quadro - proibida aqui e inútil além disso, já que o pool acabou de
       * alocar esta partícula e a sua velocidade são dois números.
       */
      const drift = residue.driftPerSecond * pad * speedScale;
      particle.velocityX = Math.cos(angle) * drift;
      particle.velocityY = Math.sin(angle) * drift * 0.7;
    }
  }

  /* ---------------------------------------------------------------------- */
  /*  O quadro                                                              */
  /* ---------------------------------------------------------------------- */

  /**
   * Um quadro, com o zoom da câmera e o tempo decorrido.
   *
   * Chamado pelo `update` da cena, que é quem sabe quando um quadro aconteceu.
   * O renderer não escuta evento nenhum por conta própria: um ouvinte a menos é
   * um vazamento a menos.
   */
  update(deltaMs: number, zoom: number): void {
    if (this.destroyed) {
      return;
    }
    this.clockMs += deltaMs;
    const diameter = pvpRunePadDiameterPx(this.padRadius, zoom);
    this.finished.length = 0;
    for (const visual of this.pads.values()) {
      visual.lod = pvpRuneLodForDiameter(diameter, visual.lod);
      if (visual.phase === 'collecting') {
        visual.collectMs += deltaMs;
      }
      this.drawPad(visual);
      if (
        visual.phase === 'collecting' &&
        visual.collectMs >= pvpRuneCollectDurationMs(visual.appearance.collect)
      ) {
        this.finished.push(visual.pad);
        continue;
      }
      if (visual.phase === 'idle') {
        this.pumpResidue(visual, deltaMs);
      }
    }
    for (const pad of this.finished) {
      this.retire(pad, 'immediate');
    }
  }

  private pumpResidue(visual: PadVisual, deltaMs: number): void {
    const lod = visual.lod ?? 'full';
    const cap = pvpRuneResidueCap(visual.appearance, lod);
    if (cap <= 0) {
      return;
    }
    const emitter = this.emitters.get(visual.kind);
    if (!emitter) {
      return;
    }
    visual.emitDebtMs += deltaMs;
    const period = visual.appearance.residue.frequencyMs * pvpRunePulseScale(lod);
    if (visual.emitDebtMs < period) {
      return;
    }
    /*
     * No máximo uma partícula por pad por período, e a dívida é ZERADA em vez
     * de decrementada.
     *
     * Uma aba escondida por quatro segundos não pode voltar e despejar quatro
     * segundos de resíduo num quadro - a mesma regra que o relógio da caçada
     * enuncia sobre progresso, aplicada a tinta.
     */
    visual.emitDebtMs = 0;
    if (emitter.getAliveParticleCount() >= cap * PVP_RUNE_PADS.length) {
      return;
    }
    this.emitResidue(visual, 1, 1);
  }

  /**
   * Um artefato, desenhado.
   *
   * Lê o relógio e as especificações; não aloca nada. Cada número abaixo é uma
   * função pura de `rune-appearance.ts`, que é o que permite testar a
   * matemática de quadro sem navegador.
   */
  private drawPad(visual: PadVisual): void {
    const appearance = visual.appearance;
    const pad = this.padRadius;
    const lod = visual.lod ?? 'full';
    const elapsed = this.clockMs + visual.phaseMs;
    const collect = appearance.collect;

    /* Os três primeiros tempos da coleta, como uma escala e um esmaecimento. */
    let sequenceScale = 1;
    let sequenceAlpha = 1;
    if (visual.phase === 'collecting') {
      const t = visual.collectMs;
      if (t < collect.anticipationMs) {
        const k = t / Math.max(1, collect.anticipationMs);
        sequenceScale = 1 + (collect.anticipationScale - 1) * k;
      } else if (t < collect.anticipationMs + collect.collapseMs) {
        const k = (t - collect.anticipationMs) / Math.max(1, collect.collapseMs);
        sequenceScale = collect.anticipationScale * (1 - k);
        sequenceAlpha = 1 - k * 0.35;
      } else {
        sequenceScale = 0;
        sequenceAlpha = 0;
      }
      this.drawFlash(visual, t);
    }

    const float = pvpRuneFloatOffset(appearance.idle, elapsed) * pad;
    /* O artefato paira; o selo abaixo dele, não. */
    const hover = -PVP_RUNE_HOVER_RADII * pad;

    for (const instance of visual.layers) {
      const spec = instance.spec;
      const image = instance.image;
      if (!pvpRuneLodAtLeast(lod, spec.minLod) || sequenceScale <= 0) {
        image.setVisible(false);
        continue;
      }
      let offsetX = spec.offsetX * pad;
      let offsetY = spec.offsetY * pad;
      let rotation = 0;
      if (spec.orbit) {
        const point = pvpRuneOrbitPoint(spec.orbit, instance.copy, elapsed);
        offsetX += point.x * pad;
        offsetY += point.y * pad * 0.72;
        if (spec.orbit.facing) {
          rotation += point.angle + Math.PI / 2;
        }
      }
      const sweep = pvpRuneSweepScale(spec, elapsed);
      const size = spec.radius * pad * 2 * sequenceScale;
      const base = size / textureSide(image);
      image
        .setVisible(true)
        .setPosition(visual.x + offsetX, visual.y + hover + offsetY + float * spec.lift)
        .setRotation(rotation)
        .setAlpha(pvpRuneLayerAlpha(spec, elapsed) * sequenceAlpha)
        .setScale(base * sweep, base * spec.squash);
    }

    /*
     * O selo: no pad, sob tudo, girando DENTRO do plano do chão.
     *
     * A ordem é o que importa. O plano recebe o achatamento e fica onde o pad
     * está; o sprite gira dentro dele, na origem local. A matriz composta vira
     * `T · S_achatamento · R · S_tamanho`, então o eixo maior da elipse é
     * horizontal para sempre e o que gira é o conteúdo - um selo mágico deitado
     * no chão. Fazer os dois no mesmo objeto dava `T · R · S`, que gira a
     * elipse já achatada e a faz varrer de um lado ao outro.
     */
    const sealRadius = pvpRuneSealRadius(appearance.seal) * pad;
    const sealBase = (sealRadius * 2) / textureSide(visual.seal);
    visual.sealPivot
      .setVisible(sequenceScale > 0)
      .setPosition(visual.x, visual.y)
      .setScale(1, appearance.seal.squash);
    visual.seal
      .setVisible(sequenceScale > 0)
      .setPosition(0, 0)
      .setRotation(appearance.seal.spinTurnsPerSecond * (elapsed / 1000) * TAU)
      .setAlpha(appearance.seal.alpha * sequenceAlpha)
      .setScale(sealBase * sequenceScale);
  }

  /**
   * Tempo três: um clarão local curto, nunca mais largo que o pad.
   *
   * `flashRadius` é uma fração do raio do pad e o compositor não o deixa passar
   * de 1 - um clarão que transbordasse o pad seria um bloom, e este projeto o
   * proíbe. Os tempos declarados ficam entre 0,34 e 0,44, o que é bem menos que
   * isso: o clarão é um evento, não uma iluminação.
   */
  private drawFlash(visual: PadVisual, t: number): void {
    const collect = visual.appearance.collect;
    const start = collect.anticipationMs + collect.collapseMs;
    const pad = this.padRadius;
    if (t < start) {
      return;
    }
    const k = Math.min(1, (t - start) / Math.max(1, collect.flashMs));
    if (!visual.flash) {
      visual.flash = this.acquire();
      visual.flash.setTexture(ensureRuneTexture(this.scene, 'core-glow'));
      visual.flash.setDepth(RUNE_DEPTH.core);
      visual.flash.setTint(visual.appearance.palette.core);
      visual.flash.setBlendMode(Phaser.BlendModes.ADD);
      /* No artefato, e não no pad: a luz sai de onde ele estava. */
      visual.flash.setPosition(visual.x, visual.y - PVP_RUNE_HOVER_RADII * pad);
      /* Tempo quatro monta no clarão: o resíduo sai junto com a luz. */
      this.emitResidue(
        visual,
        Math.min(collect.burst, visual.appearance.residue.maxParticles * 2),
        collect.burstSpeed,
      );
    }
    const radius = Math.min(1, collect.flashRadius) * pad * (0.5 + k * 0.5);
    const base = (radius * 2) / textureSide(visual.flash);
    visual.flash.setScale(base, base * 0.86).setAlpha((1 - k) * 0.85);
  }

  /* ---------------------------------------------------------------------- */
  /*  Contabilidade e fim                                                   */
  /* ---------------------------------------------------------------------- */

  census(): RuneRendererCensus {
    let particles = 0;
    for (const emitter of this.emitters.values()) {
      particles += emitter.getAliveParticleCount();
    }
    let sprites = 0;
    const drawing: Record<string, string> = {};
    const lod: Record<string, PvpRuneLod> = {};
    for (const visual of this.pads.values()) {
      sprites += visual.layers.length + 1 + (visual.flash ? 1 : 0);
      drawing[String(visual.pad)] = visual.kind;
      lod[String(visual.pad)] = visual.lod ?? 'full';
    }
    let textures = 0;
    for (const key of this.textureKeys) {
      if (this.scene.textures.exists(key)) {
        textures += 1;
      }
    }
    return {
      active: this.pads.size,
      sprites,
      containers: this.pads.size,
      pooled: this.pool.length,
      emitters: this.emitters.size,
      particles,
      textures,
      drawing,
      lod,
    };
  }

  /**
   * Devolve tudo: sprites, pool, emissores e texturas.
   *
   * Idempotente: a arena sendo fechada com um renderer vivo não pode lançar, e
   * uma segunda chamada não pode liberar duas vezes.
   */
  destroy(): { readonly destroyed: number; readonly textures: number } {
    if (this.destroyed) {
      return { destroyed: 0, textures: 0 };
    }
    this.destroyed = true;
    let destroyed = 0;
    for (const visual of this.pads.values()) {
      for (const instance of visual.layers) {
        instance.image.destroy();
        destroyed += 1;
      }
      /* O plano leva o selo consigo: destruir um container destrói os filhos. */
      visual.sealPivot.destroy();
      destroyed += 2;
      if (visual.flash) {
        visual.flash.destroy();
        destroyed += 1;
      }
    }
    this.pads.clear();
    for (const image of this.pool) {
      image.destroy();
      destroyed += 1;
    }
    this.pool.length = 0;
    for (const emitter of this.emitters.values()) {
      emitter.killAll();
      emitter.destroy();
    }
    this.emitters.clear();
    const textures = destroyRuneTextures(this.scene, this.textureKeys);
    this.lastRunes = [];
    return { destroyed, textures };
  }
}

/** O lado assado da textura que um sprite está vestindo. */
function textureSide(image: Phaser.GameObjects.Image): number {
  const source = image.texture.getSourceImage() as { width?: number };
  return source.width && source.width > 0 ? source.width : 1;
}
